<template>
    <!-- Odoo 350042: «Sign-in security» — свои способы второго фактора: passkey (имя / создан / последний вход), приложение-аутентификатор,
         коды восстановления (остаток; новые показываются один раз). Добавление и удаление — после подтверждения «это я» (reauth, 5 мин).
         Секреты (otpauth-URI, коды) живут только в данных этого окна и стираются при закрытии; в store, лог и localStorage не попадают -->
    <Dialog :visible="visible" modal :header="t('Sign-in security')" :style="{ width: '34rem' }" :breakpoints="{ '640px': '96vw' }"
            class="mfa-security-dialog" @update:visible="(v) => { if (!v) close() }">
        <div v-if="loading" class="mfa-state"><i class="pi pi-spin pi-spinner"></i> {{ t('Loading…') }}</div>

        <!-- подтверждение «это я» перед действием -->
        <div v-else-if="view === 'reauth'" class="mfa-pane" data-view="reauth">
            <div class="mfa-pane-title">{{ t('Confirm it is you') }}</div>
            <div class="mfa-muted mb-3">{{ pending ? pending.label : '' }}</div>
            <template v-if="hasPasskey && passkeySupported">
                <Button :label="t('Confirm with a passkey')" icon="pi pi-key" class="w-full" :loading="busyKey === 'reauth-passkey'" :disabled="!!busyKey" @click="reauthPasskey" />
                <div class="mfa-or"><span>{{ t('or') }}</span></div>
            </template>
            <template v-if="status && status.totp && status.totp.enabled">
                <label for="mfaReauthCode" class="mfa-label">{{ t('Code from the authenticator app') }}</label>
                <InputText id="mfaReauthCode" v-model="reauthCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6" class="w-full mb-2" placeholder="123456" @keyup.enter="reauthWith('totp')" />
                <Button :label="t('Confirm')" class="w-full mb-3" :loading="busyKey === 'reauth-totp'" :disabled="!!busyKey || reauthCode.replace(/\D/g, '').length !== 6" @click="reauthWith('totp')" />
            </template>
            <label for="mfaReauthPassword" class="mfa-label">{{ t('Password') }}</label>
            <Password id="mfaReauthPassword" v-model="reauthPassword" :feedback="false" :toggleMask="true" class="w-full mb-2" inputClass="w-full" :inputProps="{ autocomplete: 'current-password' }" @keyup.enter="reauthWith('password')" />
            <Button :label="t('Confirm with password')" class="w-full p-button-outlined" :loading="busyKey === 'reauth-password'" :disabled="!!busyKey || !reauthPassword" @click="reauthWith('password')" />
            <div class="mfa-foot"><a class="mfa-link" @click="back">{{ t('Cancel') }}</a></div>
        </div>

        <!-- подтверждение удаления, когда «это я» уже свежее -->
        <div v-else-if="view === 'confirm'" class="mfa-pane" data-view="confirm">
            <div class="mfa-pane-title">{{ pending ? pending.label : '' }}</div>
            <div class="mfa-foot">
                <a class="mfa-link" @click="back">{{ t('Cancel') }}</a>
                <Button :label="t('Remove')" class="p-button-danger" :loading="!!busyKey" :disabled="!!busyKey" @click="runPending" />
            </div>
        </div>

        <div v-else-if="view === 'totp'" class="mfa-pane" data-view="totp">
            <div class="mfa-pane-title">{{ t('Set up an authenticator app') }}</div>
            <MfaTotpSetup :uri="totpUri" :busy="busyKey === 'totp-confirm'" @confirm="totpConfirm" />
            <div class="mfa-foot"><a class="mfa-link" @click="back">{{ t('Cancel') }}</a></div>
        </div>

        <div v-else-if="view === 'codes'" class="mfa-pane" data-view="codes">
            <div class="mfa-pane-title">{{ t('Your recovery codes') }}</div>
            <div class="mfa-warn">{{ t('Save these codes now: they are shown only once. Each code signs you in once if you lose your passkey or phone. The old codes no longer work.') }}</div>
            <ol class="mfa-codes"><li v-for="c in codes" :key="c"><code>{{ c }}</code></li></ol>
            <div class="mfa-foot">
                <Button :label="copied ? t('Copied') : t('Copy')" icon="pi pi-copy" class="p-button-outlined" @click="copyCodes" />
                <Button :label="t('Download')" icon="pi pi-download" class="p-button-outlined" @click="downloadCodes" />
                <Button :label="t('Done')" @click="codesDone" />
            </div>
        </div>

        <div v-else-if="status" class="mfa-pane" data-view="list">
            <div class="mfa-muted mb-3">
                {{ status.mfa_required ? t('Your organization requires two-factor sign-in.') : t('Add a second step to protect your account: a passkey or an authenticator app.') }}
            </div>

            <div class="mfa-section">
                <div class="mfa-section-head">
                    <span><i class="pi pi-key"></i> {{ t('Passkeys') }}</span>
                    <Button v-if="passkeySupported" :label="t('Add a passkey')" icon="pi pi-plus" class="p-button-sm p-button-text" :loading="busyKey === 'passkey-add'" :disabled="!!busyKey" @click="addPasskey" />
                </div>
                <div v-if="!passkeys.length" class="mfa-muted">{{ t('No passkeys yet.') }}</div>
                <div v-for="p in passkeys" :key="p.credential_id" class="mfa-row" data-row="passkey">
                    <div class="mfa-row-main">
                        <div class="mfa-row-title">{{ p.name || t('Passkey') }}</div>
                        <div class="mfa-muted">{{ t('Added {date}', { date: fmt(p.created_at) }) }} · {{ p.last_used_at ? t('Last used {date}', { date: fmt(p.last_used_at) }) : t('Never used') }}</div>
                    </div>
                    <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" :title="t('Remove')" :disabled="!!busyKey"
                            @click="askRemove(t('Remove the passkey «{name}»?', { name: p.name || t('Passkey') }), { type: 'webauthn', credential_id: p.credential_id })" />
                </div>
            </div>

            <div class="mfa-section">
                <div class="mfa-section-head">
                    <span><i class="pi pi-mobile"></i> {{ t('Authenticator app') }}</span>
                    <span v-if="status.totp && status.totp.enabled" class="mfa-on">{{ t('On') }}</span>
                    <span v-else class="mfa-off">{{ t('Off') }}</span>
                </div>
                <div v-if="status.totp && status.totp.enabled" class="mfa-row">
                    <div class="mfa-row-main mfa-muted">{{ t('Added {date}', { date: fmt(status.totp.created_at) }) }}</div>
                    <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" :title="t('Remove')" :disabled="!!busyKey"
                            @click="askRemove(t('Turn off the authenticator app?'), { type: 'totp' })" />
                </div>
                <Button v-else :label="t('Set up')" icon="pi pi-qrcode" class="p-button-sm p-button-outlined" :loading="busyKey === 'totp-setup'" :disabled="!!busyKey" @click="setupTotp" />
            </div>

            <div class="mfa-section">
                <div class="mfa-section-head">
                    <span><i class="pi pi-ticket"></i> {{ t('Recovery codes') }}</span>
                    <span class="mfa-muted">{{ recoveryRemaining ? t('{n} left', { n: recoveryRemaining }) : t('None') }}</span>
                </div>
                <div class="mfa-row">
                    <div class="mfa-row-main mfa-muted">{{ hasFactor ? t('Use a recovery code if you lose your passkey or phone.') : t('Set up a passkey or an authenticator app first.') }}</div>
                    <Button :label="recoveryRemaining ? t('Generate new codes') : t('Generate codes')" class="p-button-sm p-button-outlined" :loading="busyKey === 'codes'" :disabled="!!busyKey || !hasFactor" @click="generateCodes" />
                </div>
            </div>
        </div>

        <div v-if="error" class="mfa-error" role="alert">{{ error }}</div>
        <div v-if="info && !error" class="mfa-info" role="status">{{ info }}</div>
    </Dialog>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { t } from '@/i18n'
import MfaTotpSetup from '@/components/MfaTotpSetup.vue'
import { mfaCall, mfaFailure, webauthnFailure, requestOptions, creationOptions, credentialToJSON, passkeySupported, passkeyDefaultName, normalizeCode } from '@/utils/mfa'

export default {
    name: 'MfaSecurityDialog',
    components: { MfaTotpSetup },
    setup() {
        return { store: useMainStore() }
    },
    data() {
        return {
            loading: false,
            status: null,
            view: 'list',      // list | reauth | confirm | totp | codes
            busyKey: '',
            error: '',
            info: '',
            pending: null,     // { label, fn } — действие, ждущее подтверждения
            reauthPassword: '',
            reauthCode: '',
            totpUri: '',
            codes: [],
            copied: false,
        }
    },
    computed: {
        visible() { return !!(this.store && this.store.mfaSecurity_open) },
        passkeySupported() { return passkeySupported() },
        passkeys() { return (this.status && this.status.webauthn_credentials) || [] },
        hasPasskey() { return this.passkeys.length > 0 },
        hasFactor() { return this.hasPasskey || !!(this.status && this.status.totp && this.status.totp.enabled) },
        recoveryRemaining() { return (this.status && this.status.recovery_codes && this.status.recovery_codes.remaining) || 0 },
    },
    watch: {
        visible(v) { if (v) this.open() },
    },
    mounted() {
        if (this.visible) this.open()   // смонтировано уже открытым
    },
    methods: {
        t,
        fmt(iso) {
            if (!iso) return '—'
            const d = new Date(iso)
            return isNaN(d) ? String(iso) : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        },
        wipe() {
            this.view = 'list'; this.busyKey = ''; this.error = ''; this.info = ''; this.pending = null
            this.reauthPassword = ''; this.reauthCode = ''; this.totpUri = ''; this.codes = []; this.copied = false
        },
        open() {
            this.wipe()
            this.status = null
            this.load()
        },
        close() {
            this.wipe()   // секреты — вон из памяти окна
            this.store.mfaSecurity_open = false
        },
        back() {
            this.view = 'list'; this.pending = null; this.error = ''; this.totpUri = ''
            this.reauthPassword = ''; this.reauthCode = ''
        },
        fail(res) {
            this.busyKey = ''
            this.error = mfaFailure(res).text
        },
        async load() {
            this.loading = true
            const res = await mfaCall('status', {})
            this.loading = false
            if (!res.ok) return this.fail(res)
            this.status = res.data
        },
        // действие, требующее свежего «это я»: 403 reauth_required → окно подтверждения, после него — повтор
        async guarded(label, fn) {
            this.error = ''; this.info = ''
            const res = await fn()
            if (res && !res.ok && res.data && res.data.error === 'reauth_required') {
                this.busyKey = ''
                this.pending = { label, fn }
                this.view = 'reauth'
                return null
            }
            return res
        },
        async runPending() {
            const p = this.pending
            if (!p) return this.back()
            this.view = 'list'
            this.pending = null
            this.error = ''
            const res = await p.fn()
            if (res && !res.ok) {
                if (res.data && res.data.error === 'reauth_required') { this.pending = p; this.view = 'reauth'; this.busyKey = ''; return }
                this.fail(res)
            }
        },
        async reauthWith(method) {
            const body = method === 'totp' ? { method, code: normalizeCode(this.reauthCode, 'totp') } : { method, password: this.reauthPassword }
            this.busyKey = 'reauth-' + method
            this.error = ''
            const res = await mfaCall('reauth', body)
            this.reauthPassword = ''; this.reauthCode = ''
            if (!res.ok) return this.fail(res)
            this.busyKey = ''
            if (this.status) this.status.reauth_valid = true
            this.runPending()
        },
        async reauthPasskey() {
            this.busyKey = 'reauth-passkey'
            this.error = ''
            const begin = await mfaCall('webauthn/authenticate/begin', { purpose: 'reauth' })
            if (!begin.ok) return this.fail(begin)
            let cred
            try {
                cred = await navigator.credentials.get({ publicKey: requestOptions(begin.data.publicKey) })
            } catch (e) {
                this.busyKey = ''
                this.error = webauthnFailure(e).text
                return
            }
            const res = await mfaCall('webauthn/authenticate/finish', { purpose: 'reauth', challenge_id: begin.data.challenge_id, credential: credentialToJSON(cred) })
            if (!res.ok) return this.fail(res)
            this.busyKey = ''
            if (this.status) this.status.reauth_valid = true
            this.runPending()
        },

        addPasskey() {
            const fn = async () => {
                this.busyKey = 'passkey-add'
                const begin = await mfaCall('webauthn/register/begin', {})
                if (!begin.ok) { this.busyKey = ''; return begin }
                let cred
                try {
                    cred = await navigator.credentials.create({ publicKey: creationOptions(begin.data.publicKey) })
                } catch (e) {
                    this.busyKey = ''
                    this.error = webauthnFailure(e).text
                    return { ok: true }
                }
                const res = await mfaCall('webauthn/register/finish', { challenge_id: begin.data.challenge_id, name: passkeyDefaultName(), credential: credentialToJSON(cred) })
                this.busyKey = ''
                if (res.ok) await this.afterEnrol(t('The passkey is added.'))
                return res
            }
            this.guarded(t('Add a passkey'), fn).then((res) => { if (res && !res.ok) this.fail(res) })
        },
        setupTotp() {
            const fn = async () => {
                this.busyKey = 'totp-setup'
                const res = await mfaCall('totp/setup', {})
                this.busyKey = ''
                if (res.ok) { this.totpUri = res.data.otpauth_uri || ''; this.view = 'totp' }
                return res
            }
            this.guarded(t('Set up an authenticator app'), fn).then((res) => { if (res && !res.ok) this.fail(res) })
        },
        async totpConfirm(code) {
            this.busyKey = 'totp-confirm'
            this.error = ''
            const res = await mfaCall('totp/confirm', { code })
            this.busyKey = ''
            if (!res.ok) return this.fail(res)
            this.totpUri = ''
            this.view = 'list'
            await this.afterEnrol(t('The authenticator app is on.'))
        },
        // первый фактор подключён и кодов восстановления нет — сразу предложить их
        async afterEnrol(msg) {
            await this.load()
            this.info = msg + (this.recoveryRemaining ? '' : ' ' + t('Now generate recovery codes in case you lose it.'))
        },
        generateCodes() {
            const fn = async () => {
                this.busyKey = 'codes'
                const res = await mfaCall('recovery-codes/generate', {})
                this.busyKey = ''
                if (res.ok) { this.codes = res.data.codes || []; this.view = 'codes'; this.copied = false }
                return res
            }
            this.guarded(t('Generate new recovery codes'), fn).then((res) => { if (res && !res.ok) this.fail(res) })
        },
        askRemove(label, body) {
            this.error = ''; this.info = ''
            const fn = async () => {
                this.busyKey = 'remove'
                const res = await mfaCall('credentials/remove', body)
                this.busyKey = ''
                if (res.ok) { this.view = 'list'; await this.load(); this.info = t('Removed.') }
                return res
            }
            this.pending = { label, fn }
            // удаление — всегда после явного подтверждения: «это я» не свежее — окно reauth, иначе — «Remove?»
            this.view = this.status && this.status.reauth_valid ? 'confirm' : 'reauth'
        },
        codesText() {
            return t('WebSCM recovery codes') + '\n' + (this.store?.user?.email || '') + '\n\n' + this.codes.join('\n') + '\n'
        },
        async copyCodes() {
            try { await navigator.clipboard.writeText(this.codesText()); this.copied = true } catch (e) { this.error = t('Could not copy. Select the codes and copy them manually.') }
        },
        downloadCodes() {
            const url = URL.createObjectURL(new Blob([this.codesText()], { type: 'text/plain' }))
            const a = document.createElement('a')
            a.href = url
            a.download = 'webscm-recovery-codes.txt'
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => URL.revokeObjectURL(url), 1000)
        },
        async codesDone() {
            this.codes = []
            this.view = 'list'
            await this.load()
        },
    },
}
</script>

<style scoped>
.mfa-state { display: flex; gap: 0.5rem; align-items: center; color: #86868b; }
.mfa-pane-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.75rem; }
.mfa-muted { color: #86868b; font-size: 0.9rem; }
.mfa-label { display: block; color: #86868b; font-size: 0.9rem; margin: 0.5rem 0 0.25rem; }
.mfa-section { border-top: 1px solid var(--surface-d, #dee2e6); padding: 0.75rem 0; }
.mfa-section-head { display: flex; justify-content: space-between; align-items: center; font-weight: 600; margin-bottom: 0.5rem; }
.mfa-section-head .pi { margin-right: 0.4rem; }
.mfa-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.25rem 0; }
.mfa-row-main { min-width: 0; flex: 1; }
.mfa-row-title { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mfa-on { color: #2e9d57; font-weight: 600; }
.mfa-off { color: #86868b; }
.mfa-or { text-align: center; color: #86868b; margin: 0.75rem 0; }
.mfa-foot { display: flex; justify-content: flex-end; align-items: center; gap: 0.5rem; margin-top: 1rem; flex-wrap: wrap; }
.mfa-link { cursor: pointer; color: var(--primary-color, #2997ff); margin-right: auto; }
.mfa-warn { background: rgba(255, 193, 7, 0.15); border-left: 3px solid #f5b400; padding: 0.5rem 0.75rem; font-size: 0.9rem; margin-bottom: 0.75rem; }
.mfa-codes { columns: 2; font-size: 1rem; padding-left: 1.5rem; margin: 0; user-select: all; }
.mfa-codes li { margin: 0.2rem 0; }
.mfa-error { color: #d9534f; margin-top: 0.75rem; }
.mfa-info { color: #2e9d57; margin-top: 0.75rem; }
</style>
